/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = Object.create(null);
      this.requestId = 0;
      this.dismissed = false;
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = this.querySelector('[name="filter.p.product_type"]');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      const debouncedInput = debounce(() => {
        if (!this.dismissed) this.handleInput();
      });
      this.input.addEventListener('input', () => {
        this.dismissed = false;
        this.requestId += 1;
        this.requestController?.abort();
        this.removeAttribute('results');
        this.input.setAttribute('aria-activedescendant', '');
        this.results.querySelectorAll('.predictive-search__item[aria-selected="true"]')
          .forEach((item) => item.setAttribute('aria-selected', 'false'));
        if (!this.getQuery().length) {
          this.close();
          return;
        }
        debouncedInput();
      });
      this.keydownHandler = this.handleKeydown.bind(this);
      this.keyupHandler = this.handleKeyup.bind(this);
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('[role="tabpanel"]:not([hidden]) .predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      this.dismissed = false;
      this.removeAttribute('results');
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      this.dismissed = false;
      this.getResults(searchTerm);
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.closest('.tablist__tab') || evt.isComposing) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          if (this.hasAttribute('open')) {
            evt.preventDefault();
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          if (evt.target === this.input) this.selectOption(evt);
          break;

        case 'Escape':
          if (this.hasAttribute('open')) evt.preventDefault();
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      let allResults = Array.from(this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      ));
      if (!allResults.length) {
        allResults = Array.from(this.querySelectorAll(
          '.predictive-search__suggestions .predictive-search__item'
        ));
      }
      if (!allResults.length) return;
      const selectedIndex = allResults.findIndex((item) => item.getAttribute('aria-selected') === 'true');
      const selectedResult = allResults[selectedIndex];
      const nextIndex = key === 'ArrowUp'
        ? (selectedIndex <= 0 ? allResults.length - 1 : selectedIndex - 1)
        : (selectedIndex + 1) % allResults.length;
      const resultToSelect = allResults[nextIndex];

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        this.results.querySelectorAll('.predictive-search__item[aria-selected="true"]')
          .forEach((item) => item.setAttribute('aria-selected', 'false'));
        resultToSelect.setAttribute('aria-selected', 'true');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
        resultToSelect.scrollIntoView({ block: 'nearest' });
      }
    }

    /**
     * Selects a result
     */
    selectOption(evt) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item[aria-selected="true"] > .js-search-link'
      ) || this.querySelector('.predictive-search__suggestions .predictive-search__item[aria-selected="true"] > a');
      if (selectedResult) {
        evt.preventDefault();
        selectedResult.click();
      }
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      const requestId = ++this.requestId;
      this.requestController?.abort();
      const productType = this.productTypeInput?.value || '';
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=10`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=each`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams;
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      this.requestController = new AbortController();
      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`, {
          signal: this.requestController.signal
        });
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        if (!resultsEl) throw new Error('Missing predictive search results');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        if (requestId !== this.requestId || this.dismissed || this.getQuery() !== searchTerm
          || (this.productTypeInput?.value || '') !== productType) return;
        this.renderResults(resultsMarkup);
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.requestId) return;
        this.removeAttribute('results');
        this.close();
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      clearTimeout(this.statusTimer);
      this.statusTimer = setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.results.querySelectorAll('.predictive-search__item').forEach((item, index) => {
        item.id = `${this.input.id}-option-${index}`;
        item.setAttribute('aria-selected', 'false');
      });
      this.input.setAttribute('aria-activedescendant', '');
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      this.dismissed = true;
      this.requestId += 1;
      this.requestController?.abort();
      clearTimeout(this.statusTimer);
      this.statusEl.textContent = '';
      this.statusEl.setAttribute('aria-hidden', 'true');
      // If reset button was clicked, empty the input field.
      if (evt && this.resetBtn.contains(evt.target)) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      if (!document.querySelector('predictive-search[open]')) {
        document.body.classList.remove('overlay-predictive-search');
      }

      // Remove event listeners added on search results opening.
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
